import * as React from 'react';
import { useIntl } from 'react-intl';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import TooltipComponent from '../../TooltipComponent';

interface IChipSelectProps {
  id: string;
  name: string;
  label: string;
  control: Control<any>;
  note?: string;
  // placeholder?: string;
  required?: boolean;
  // onFocusCallback?: () => void;
  onClickItemCallback?: (event: any) => void;
  onClickCallback?: (event: any) => void;
  onClickRemoveItemCallback?: (items: IChipInputItem[]) => void;
  tooltip?: string;
  labelTooltip?: string;
  warningMessage?: string;
}

export interface IChipInputItem {
  id: number | string;
  label: number | string;
  passed?: boolean;
  IChipInputItem?: boolean;
  type?: number | string;
}

export default function ChipInput(props: IChipSelectProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: '', // or the default value
  });

  const handleRemove = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    const valueToRemove = event.currentTarget.getAttribute('data-value');
    const newValues = value.filter(
      (valueItem: IChipInputItem) => String(valueItem.id) !== valueToRemove
    );
    onChange(newValues);

    if (props.onClickRemoveItemCallback) {
      props.onClickRemoveItemCallback(newValues);
    }
  };

  return (
    <div
      className={`chip-input-component form-group ${props.warningMessage && 'form-group-warning'} ${
        error && 'form-group-error'
      }`}
    >
      <div>
        <label className="d-block form-label">
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
          {props.note && (
            <>
              <br />
              <small>{props.note}</small>
            </>
          )}
        </label>
        <div className="d-block" title={props.tooltip || ''}>
          <div
            className="form-control-labels"
            onClick={props.onClickCallback}
            data-cy={`chip-input-${props.name}`}
          >
            {value.map((valueItem: IChipInputItem, index: number) => (
              <div
                className={`label ${valueItem.passed === false ? 'label-primary ' : ''}`}
                key={`${valueItem.id}-${index}`}
                title={valueItem.passed === false ? '' : props.labelTooltip}
                onClick={valueItem.passed === false ? undefined : props.onClickItemCallback}
                id={`chip-${props.name}`}
                data-value={valueItem.id}
                data-cy={`chip-${props.name}`}
              >
                {valueItem.passed === false ? (
                  <TooltipComponent
                    id={`${valueItem.id}-${index}-tooltip`}
                    key={`${valueItem.id}-${index}-tooltip`}
                    top="25px"
                    overlay={
                      <div style={{ color: '#fff' }}>
                        {f({ id: 'alarms.chipinput.tooltip.bad.rule' })}
                      </div>
                    }
                  >
                    <div className="d-flex align-items-center">
                      <i className="cmd-icon cmd-icon-exclamation" />
                      <span
                        id={`chip-${props.name}-false`}
                        data-value={valueItem.id}
                        data-cy={`chip-${props.name}-false`}
                      >
                        {`${valueItem.label} `}
                      </span>
                    </div>
                  </TooltipComponent>
                ) : (
                  <span>{`${valueItem.label} `}</span>
                )}
                <a
                  id={`chip-icon-${props.name}`}
                  className="label-action cmd-icon cmd-icon-remove-sm"
                  onClick={handleRemove}
                  data-value={valueItem.id}
                  data-cy={`chip-remove-${props.name}`}
                >
                  {/* Content */}
                </a>
              </div>
            ))}
          </div>
          {error && error.message && <CmdError message={error.message} />}
          {props.warningMessage && <CmdError message={props.warningMessage} />}
        </div>
      </div>
    </div>
  );
}
